import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI, propertyAPI, crmAPI, crmSyncAPI, adminAuthAPI } from '../services/api';
import { registerDeviceToken, unregisterDeviceToken, onForegroundMessage } from '../services/pushNotificationService';
import {
  setToken, getToken, removeToken,
  setUserData, getUserData, removeUserData,
  setPropertyData, getPropertyData, removePropertyData,
  setOtpSession, getOtpSession, clearOtpSession,
  clearAll, hasValidToken,
} from '../services/tokenService';
import { clearBookingsCache } from '../services/offlineStorage';
import { toast } from '../hooks/use-toast';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize from sessionStorage
  useEffect(() => {
    const token = getToken();
    const savedUser = getUserData();
    const savedProperty = getPropertyData();

    if (token && savedUser) {
      try {
        setUser(savedUser);
        if (savedUser.properties && savedUser.properties.length > 0) {
          setProperties(savedUser.properties);
          if (savedProperty) {
            setSelectedProperty(savedProperty);
          } else {
            setSelectedProperty(savedUser.properties[0]);
          }
        }
      } catch (e) {
        console.error('Error parsing saved user data:', e);
        clearAll();
      }
    }
    setLoading(false);
  }, []);

  // Phone-only login Step 1: Send OTP
  const login = useCallback(async (phoneNumber) => {
    setLoading(true);
    setError(null);
    try {
      console.log('[Auth] Requesting WhatsApp OTP for:', phoneNumber);

      // iter121 — WhatsApp OTP replaces 2Factor SMS. New endpoints under
      // /auth/mobile/*. Session behaviour + response shape stay the same.
      const response = await authAPI.sendMobileOtp(phoneNumber);

      console.log('[Auth] WhatsApp OTP request response:', response);

      if (response.STATUS === 1) {
        const sessionId = response.RESULT.session_id;
        const mobile = response.RESULT.mobile || phoneNumber;
        if (sessionId) {
          setOtpSession(sessionId, mobile);
        }
        return {
          success: true,
          requires_otp: true,
          session_id: sessionId,
          phone: mobile,
          cooldown_seconds: response.RESULT.cooldown_seconds || 30,
          user_message: response.RESULT.user_message,
        };
      }
      return { success: false, message: response.MESSAGE || 'Your number is not authorized. Please contact support.' };
    } catch (err) {
      const message = err.detail || err.message || 'Your number is not authorized. Please contact support.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Phone login Step 2: Verify OTP (iter121 — WhatsApp OTP)
  const verifyOtp = useCallback(async (phoneNumber, sessionId, otp) => {
    setLoading(true);
    setError(null);
    try {
      console.log('[Auth] Verifying WhatsApp OTP...', { phone: phoneNumber });

      // sessionId is preserved for backward-compat with callers, but the new
      // endpoint keys off the mobile + latest live OTP record server-side.
      const response = await authAPI.verifyMobileOtp(phoneNumber, otp);
      
      console.log('[Auth] OTP verification response:', response);
      
      if (response.STATUS === 1) {
        const result = response.RESULT || {};

        // Phone-side force-password-change: backend returned temp_token instead of JWT
        if (result.requires_password_change && result.temp_token) {
          console.log('[Auth][phone] requires_password_change');
          return {
            success: true,
            requires_password_change: true,
            temp_token: result.temp_token,
            ownerName: result.owner_name,
            email: result.email,
            message: result.user_message,
          };
        }

        const token = result.access_token || result.token;
        const userData = result.user || result;
        const userProperties = userData.properties || [];
        
        if (!token) {
          console.error('[Auth] No token in response');
          return { success: false, message: 'Login failed - no token received' };
        }
        
        // Store token and user data
        setToken(token);
        setUserData(userData);
        
        // Clear OTP session
        clearOtpSession();
        
        setUser(userData);
        setProperties(userProperties);
        
        if (userProperties && userProperties.length > 0) {
          setSelectedProperty(userProperties[0]);
          setPropertyData(userProperties[0]);
          
          // Trigger auto-sync after login (silent, non-blocking)
          try {
            await crmSyncAPI.autoSyncAll();
          } catch (syncError) {
            console.log('Post-login sync:', syncError?.message || 'Sync skipped');
          }
          
          // Register for push notifications (silent, non-blocking)
          try {
            const pushResult = await registerDeviceToken(userProperties[0].id);
            if (pushResult.success) {
              console.log('Push notifications registered');
              onForegroundMessage((payload) => {
                console.log('Notification received:', payload);
              });
            }
          } catch (pushError) {
            console.log('Push registration:', pushError?.message || 'Skipped');
          }
        }
        
        console.log('[Auth] Login successful:', userData.name);
        return { success: true, user: userData };
      }
      return { success: false, message: response.MESSAGE || 'OTP verification failed' };
    } catch (err) {
      console.error('[Auth] OTP verification error:', err);
      const message = err.detail || err.message || 'OTP verification failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Email + Password login. Returns one of:
  //   { success:true, requires_password_change:true, temp_token, email, ownerName }
  //   { success:true, user, requires_password_change:false }
  //   { success:false, message }
  const loginWithEmail = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      console.log('[Auth][login_method=email] attempt');
      const response = await authAPI.loginWithEmail(email, password);

      if (response.STATUS !== 1) {
        return { success: false, message: response.MESSAGE || 'Login failed' };
      }
      const result = response.RESULT || {};

      if (result.requires_password_change) {
        return {
          success: true,
          requires_password_change: true,
          temp_token: result.temp_token,
          email: result.email,
          ownerName: result.owner_name,
          message: result.user_message,
        };
      }

      const token = result.access_token;
      const userData = result.user || {};
      const userProperties = userData.properties || [];
      if (!token) {
        return { success: false, message: 'Login failed — no token received' };
      }
      setToken(token);
      setUserData(userData);
      setUser(userData);
      setProperties(userProperties);
      if (userProperties.length > 0) {
        setSelectedProperty(userProperties[0]);
        setPropertyData(userProperties[0]);
      }
      // Fire-and-forget post-login sync + push registration (best-effort)
      try { await crmSyncAPI.autoSyncAll(); } catch (_) { /* silent */ }
      if (userProperties.length > 0) {
        try { await registerDeviceToken(userProperties[0].id); } catch (_) { /* silent */ }
      }
      return { success: true, user: userData };
    } catch (err) {
      const message = err.detail || err.message || 'Login failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Submit a new password using the temp_token from force-password-change handoff
  const changePassword = useCallback(async (tempToken, newPassword) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.changePassword(tempToken, newPassword);
      if (response.STATUS !== 1) {
        return { success: false, message: response.MESSAGE || 'Password change failed' };
      }
      const result = response.RESULT || {};
      const token = result.access_token;
      const userData = result.user || {};
      const userProperties = userData.properties || [];
      if (!token) {
        return { success: false, message: 'Password updated but no token received' };
      }
      setToken(token);
      setUserData(userData);
      setUser(userData);
      setProperties(userProperties);
      if (userProperties.length > 0) {
        setSelectedProperty(userProperties[0]);
        setPropertyData(userProperties[0]);
      }
      return { success: true, user: userData };
    } catch (err) {
      const message = err.detail || err.message || 'Password change failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Internal Admin (BFR) login — email + password. Isolated from owner flows.
  const loginAsAdmin = useCallback(async (email, password) => {
    setLoading(true);
    setError(null);
    try {
      const response = await adminAuthAPI.login(email, password);
      if (!response || response.STATUS !== 1) {
        return { success: false, message: response?.MESSAGE || 'Login failed' };
      }
      const result = response.RESULT || {};
      const token = result.access_token;
      const userData = result.user || {};
      if (!token) {
        return { success: false, message: 'Login failed — no token received' };
      }
      setToken(token);
      setUserData(userData);
      setUser(userData);
      setProperties([]);
      setSelectedProperty(null);
      return { success: true, user: userData };
    } catch (err) {
      const message = err.detail || err._friendlyMessage || err.message || 'Login failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  // Resend OTP — iter121 uses the same /auth/mobile/send-otp endpoint which
  // honours the 30s cooldown and issues a fresh session_id.
  const resendOtp = useCallback(async (phoneNumber, _sessionId) => {
    try {
      console.log('[Auth] Resending WhatsApp OTP...');
      const response = await authAPI.resendMobileOtp(phoneNumber);

      if (response.STATUS === 1) {
        const newSessionId = response.RESULT.session_id;
        if (newSessionId) {
          setOtpSession(newSessionId, phoneNumber);
        }
        return {
          success: true,
          session_id: newSessionId,
          cooldown_seconds: response.RESULT.cooldown_seconds || 30,
          user_message: response.RESULT.user_message
        };
      }
      return { success: false, message: response.MESSAGE || 'Failed to resend OTP' };
    } catch (err) {
      const message = err.detail || err.message || 'Failed to resend OTP';
      return { success: false, message };
    }
  }, []);

  const register = useCallback(async (userData) => {
    setLoading(true);
    setError(null);
    try {
      const response = await authAPI.register(userData);
      if (response.STATUS === 1) {
        const { token, ...user } = response.RESULT;
        setToken(token);
        setUserData(response.RESULT);
        setUser(response.RESULT);
        return { success: true };
      }
      return { success: false, message: response.MESSAGE || 'Registration failed' };
    } catch (err) {
      const message = err.detail || err.message || 'Registration failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    const adminSession = (user?.role || '').toLowerCase() === 'superhost_admin';
    // Internal admin (BFR) sessions are read-only & never register FCM — skip
    // server-side cleanup (those writes are blocked by the read-only guard).
    if (!adminSession) {
      try {
        await unregisterDeviceToken();
      } catch (pushError) {
        console.log('Push unregister:', pushError?.message || 'Skipped');
      }
      try {
        await authAPI.logout();
      } catch (e) {
        console.error('Logout error:', e);
      }
    }
    clearAll();
    setUser(null);
    setProperties([]);
    setSelectedProperty(null);
  }, [user]);

  const switchProperty = useCallback((property) => {
    setSelectedProperty(property);
    setPropertyData(property);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      if (response.STATUS === 1) {
        setUser(response.RESULT);
        setProperties(response.RESULT.properties || []);
        setUserData(response.RESULT);
      }
    } catch (e) {
      console.error('Error refreshing profile:', e);
    }
  }, []);

  /**
   * iter148 — Silent, cleanup-aware profile refresh used to detect that a
   * property the user previously had access to has been deleted upstream
   * (CRM → Admin Panel → property_users.is_active=false). Called on boot
   * and periodically so deletions propagate to the App without a manual
   * logout. Non-blocking: never throws, never blocks the UI.
   *
   * On property removal we:
   *  - Update state + sessionStorage with the fresh list.
   *  - Auto-switch `selectedProperty` if it was the one removed.
   *  - Purge the global bookings cache so Calendar/Analytics can't render
   *    stale rows for the removed property.
   *  - Fire an in-app toast so the owner isn't blindsided.
   *  - If ALL properties are gone, force logout (nothing left to view).
   */
  const refreshPropertiesSilently = useCallback(async () => {
    // Only run when we have a session + it's an owner (BFR admin has no
    // property list to reconcile).
    if (!hasValidToken()) return;
    let response;
    try {
      response = await authAPI.getProfile();
    } catch {
      return; // network hiccup — try again next tick
    }
    if (!response || response.STATUS !== 1 || !response.RESULT) return;

    const fresh = response.RESULT;
    const freshProps = Array.isArray(fresh.properties) ? fresh.properties : [];
    const freshIds = new Set(
      freshProps.map((p) => p?.id || p?.property_id).filter(Boolean)
    );

    // Compare against the props currently in state (source of truth for UI).
    const currentIds = properties
      .map((p) => p?.id || p?.property_id)
      .filter(Boolean);
    const removedIds = currentIds.filter((id) => !freshIds.has(id));

    // Always update the merged user (name/email/role could also drift).
    setUser((prev) => ({ ...(prev || {}), ...fresh, properties: freshProps }));
    setProperties(freshProps);
    setUserData({ ...(fresh || {}), properties: freshProps });

    if (!removedIds.length) return;

    console.warn('[Auth] Properties removed upstream:', removedIds);

    // Property list changed — purge caches keyed on the removed property.
    try { clearBookingsCache(); } catch { /* noop */ }

    // If the currently selected property is now gone, switch or logout.
    const selectedId = selectedProperty?.id || selectedProperty?.property_id;
    if (selectedId && removedIds.includes(selectedId)) {
      if (freshProps.length) {
        const next = freshProps[0];
        setSelectedProperty(next);
        setPropertyData(next);
        try {
          toast({
            title: 'Property access updated',
            description: 'The property you were viewing is no longer available. Switched to your first available property.',
          });
        } catch { /* noop */ }
      } else {
        // No properties left — user has been fully offboarded.
        try {
          toast({
            title: 'Access revoked',
            description: 'You no longer have access to any properties. Signing you out.',
          });
        } catch { /* noop */ }
        clearAll();
        setUser(null);
        setProperties([]);
        setSelectedProperty(null);
      }
    } else {
      try {
        toast({
          title: 'Property list updated',
          description: `${removedIds.length} propert${removedIds.length === 1 ? 'y was' : 'ies were'} removed.`,
        });
      } catch { /* noop */ }
    }
  }, [properties, selectedProperty]);

  const isAuthenticated = !!user && hasValidToken();
  const isAdmin = !!user && (user.role || '').toLowerCase() === 'superhost_admin';

  // iter148 — Once we've hydrated a session from storage, fire a silent
  // refresh so any property that was deleted upstream while the app was
  // closed disappears immediately (no manual logout required).
  const didBootRefreshRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || isAdmin || didBootRefreshRef.current) return;
    didBootRefreshRef.current = true;
    // Non-blocking — Calendar / Property Selector will re-render as state settles.
    refreshPropertiesSilently();
  }, [isAuthenticated, isAdmin, refreshPropertiesSilently]);

  // iter148 — Periodic property-list reconciliation. Rides alongside the
  // 60s heartbeat cadence below; timings are independent so a brief network
  // stumble on one doesn't wedge the other.
  useEffect(() => {
    if (!isAuthenticated || isAdmin) return;
    const id = setInterval(() => { refreshPropertiesSilently(); }, 60_000);
    return () => clearInterval(id);
  }, [isAuthenticated, isAdmin, refreshPropertiesSilently]);

  // iter148 — Refresh on visibility gain (app foregrounded, tab switched
  // back). Mobile / Capacitor throttle background intervals, so relying on
  // the 60s poll alone would let deletions linger until the next foreground
  // + 60s. This bridges that gap without introducing extra network traffic
  // in the steady state.
  useEffect(() => {
    if (!isAuthenticated || isAdmin) return;
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        refreshPropertiesSilently();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [isAuthenticated, isAdmin, refreshPropertiesSilently]);

  // Activity heartbeat — sends user_id + lastActiveAt to backend every 60 seconds
  useEffect(() => {
    if (!isAuthenticated || !user) return;
    
    const userId = user.user_id || user.phone || user.mobile_number;
    if (!userId) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    const sendHeartbeat = async () => {
      try {
        const token = getToken();
        await fetch(`${API_URL}/api/activity/heartbeat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ user_id: userId }),
        });
      } catch (e) {
        // Silent fail — heartbeat is non-critical
      }
    };
    
    // Send immediately on login
    sendHeartbeat();
    
    // Then every 60 seconds
    const interval = setInterval(sendHeartbeat, 60000);
    
    return () => clearInterval(interval);
  }, [isAuthenticated, user]);

  return (
    <AuthContext.Provider
      value={{
        user,
        properties,
        selectedProperty,
        setSelectedProperty: switchProperty,
        loading,
        error,
        isAuthenticated,
        isAdmin,
        login,
        verifyOtp,
        resendOtp,
        loginWithEmail,
        loginAsAdmin,
        changePassword,
        register,
        logout,
        switchProperty,
        refreshProfile,
        refreshPropertiesSilently,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
